import { useState, type FormEvent } from 'react';
import { submitFormToEndpoint } from '@/lib/formSubmit';
import { contactCategories } from '@/mocks/site';

const ENDPOINT = 'https://readdy.ai/api/form/dbacmfm9if4sq287d0ng';

const inputClass =
  'w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-[13px] text-foreground-900 placeholder:text-foreground-400 outline-none transition-all duration-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30';

const labelClass = 'flex items-center gap-2 text-[12px] font-bold text-foreground-800';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    setErrorMessage('');
    setStatus('submitting');

    const result = await submitFormToEndpoint(formEl, ENDPOINT, 'お問い合わせ');

    if (result.ok) {
      setStatus('success');
      formEl.reset();
      setMessageLength(0);
      return;
    }

    setErrorMessage(result.message);
    setStatus('idle');
  };

  if (status === 'success') {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-lg border border-background-300 bg-background-50 px-6 py-14 text-center md:px-10 md:py-20">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-check-line text-2xl" aria-hidden="true" />
        </span>
        <h3 className="mt-6 font-heading text-[20px] font-black text-foreground-950">送信が完了しました</h3>
        <p className="mt-3 max-w-sm text-[12.5px] leading-[1.9] text-foreground-600">
          お問い合わせありがとうございます。内容を確認のうえ、担当よりご連絡いたします。
        </p>
        <p className="mt-4 max-w-sm text-[11px] leading-relaxed text-foreground-400">
          ※ 自動返信メールは設定準備中です。お急ぎの場合は公式LINEまたはお電話でご連絡ください。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-7 cursor-pointer rounded-md border border-foreground-300 px-5 py-2.5 text-[12px] font-bold text-foreground-700 transition-colors duration-300 hover:bg-background-100"
        >
          フォームに戻る
        </button>
      </div>
    );
  }

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="rounded-lg border border-background-300 bg-background-50 p-5 md:p-8">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-heading text-[17px] font-black text-foreground-950 md:text-[19px]">お問い合わせフォーム</h3>
        <p className="text-[10px] en-label text-foreground-400">CONTACT</p>
      </div>
      <p className="mt-2 text-[11.5px] leading-relaxed text-foreground-500">
        <span className="text-primary-600">*</span> は必須項目です。
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-2 md:gap-5">
        <div className="md:col-span-2">
          <label className={labelClass} htmlFor="contact_category">
            お問い合わせ種別 <span className="text-[10px] font-bold text-primary-600">必須</span>
          </label>
          <select
            id="contact_category"
            name="contact_category"
            required
            defaultValue=""
            className={`${inputClass} mt-2 cursor-pointer`}
          >
            <option value="" disabled>
              選択してください
            </option>
            {contactCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="contact_name">
            お名前 <span className="text-[10px] font-bold text-primary-600">必須</span>
          </label>
          <input id="contact_name" name="contact_name" required className={`${inputClass} mt-2`} placeholder="山田 花子" />
        </div>

        <div>
          <label className={labelClass} htmlFor="contact_company">
            会社名・屋号 <span className="text-[10px] text-foreground-400">任意</span>
          </label>
          <input id="contact_company" name="contact_company" className={`${inputClass} mt-2`} placeholder="株式会社〇〇" />
        </div>

        <div>
          <label className={labelClass} htmlFor="contact_email">
            メールアドレス <span className="text-[10px] font-bold text-primary-600">必須</span>
          </label>
          <input
            id="contact_email"
            name="email"
            type="email"
            required
            className={`${inputClass} mt-2`}
            placeholder="example@mail.com"
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="contact_phone">
            電話番号 <span className="text-[10px] font-bold text-primary-600">必須</span>
          </label>
          <input
            id="contact_phone"
            name="contact_phone"
            type="tel"
            required
            inputMode="tel"
            className={`${inputClass} mt-2`}
            placeholder="090-0000-0000"
          />
        </div>

        <div className="md:col-span-2">
          <label className={labelClass} htmlFor="contact_line">
            LINE ID <span className="text-[10px] text-foreground-400">任意</span>
          </label>
          <input id="contact_line" name="contact_line" className={`${inputClass} mt-2`} placeholder="line_id" />
        </div>

        <div className="md:col-span-2">
          <label className={labelClass} htmlFor="contact_message">
            お問い合わせ内容 <span className="text-[10px] font-bold text-primary-600">必須</span>
          </label>
          <textarea
            id="contact_message"
            name="contact_message"
            required
            maxLength={500}
            rows={5}
            onChange={(event) => setMessageLength(event.target.value.length)}
            className={`${inputClass} mt-2 resize-none leading-relaxed`}
            placeholder="ご相談内容をご記入ください。"
          />
          <p className="mt-1 text-right text-[10.5px] text-foreground-400">{messageLength} / 500</p>
        </div>
      </div>

      <div className="relative mt-4 overflow-hidden">
        <label className="hp-field" aria-hidden="true">
          contact_alt
          <input name="contact_alt" type="text" tabIndex={-1} autoComplete="off" readOnly className="hp-field" />
        </label>
      </div>

      {errorMessage && (
        <div role="alert" className="mt-5 rounded-md border border-primary-300 bg-primary-50 px-4 py-3">
          <p className="text-[12px] leading-relaxed text-primary-800">{errorMessage}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-foreground-950 px-6 py-4 text-[13px] font-bold text-background-50 transition-colors duration-300 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
            送信中...
          </>
        ) : (
          <>
            送信する
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </>
        )}
      </button>

      <p className="mt-3 text-[10.5px] leading-relaxed text-foreground-400">
        送信いただいた内容は、お問い合わせ対応の目的にのみ使用します。
      </p>
    </form>
  );
}